(() => {
  const setExpanded = (button, expanded) => {
    if (button) button.setAttribute('aria-expanded', expanded ? 'true' : 'false');
  };

  const closeFooterLanguagePickers = (except = null, restoreFocus = false) => {
    document.querySelectorAll('.sv-oddit-footer-language-picker[open]').forEach((picker) => {
      if (picker === except) return;
      picker.open = false;
      if (restoreFocus) picker.querySelector('summary')?.focus();
    });
  };

  document.addEventListener('click', (event) => {
    const clickedPicker = event.target.closest?.('.sv-oddit-footer-language-picker') || null;
    closeFooterLanguagePickers(clickedPicker);
  });

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') closeFooterLanguagePickers(null, true);
  });

  const essentialImages = new WeakMap();
  const getEssentialImage = (section, source, currentImage) => {
    let images = essentialImages.get(section);
    if (!images) {
      images = new Map();
      essentialImages.set(section, images);
    }
    if (!images.has(source)) {
      const image = currentImage.getAttribute('src') === source ? currentImage : currentImage.cloneNode(false);
      if (image !== currentImage) {
        image.loading = 'eager';
        image.decoding = 'async';
        image.fetchPriority = 'low';
        image.src = source;
      }
      images.set(source, image);
    }
    return images.get(source);
  };

  const warmEssentialImages = (section) => {
    const currentImage = section.querySelector('[data-sv-essential-detail-image]');
    if (!currentImage) return;
    section.querySelectorAll('[data-sv-feature-image]').forEach((hotspot) => {
      const source = hotspot.dataset.svFeatureImage;
      if (!source) return;
      const image = getEssentialImage(section, source, currentImage);
      image.loading = 'eager';
      image.decode?.().catch(() => {});
    });
  };

  const selectEssential = (section, index) => {
    if (!section) return;
    const hotspots = [...section.querySelectorAll('[data-sv-feature-index]')];
    if (!hotspots.length) return;
    const safeIndex = (index + hotspots.length) % hotspots.length;
    const activeHotspot = hotspots[safeIndex];
    section.dataset.svEssentialIndex = String(safeIndex);
    hotspots.forEach((button) => {
      const active = button === activeHotspot;
      button.classList.toggle('is-active', active);
      button.setAttribute('aria-pressed', active ? 'true' : 'false');
    });

    const detail = section.querySelector('[data-sv-essential-detail]');
    const detailImage = detail?.querySelector('[data-sv-essential-detail-image]');
    const detailTitle = detail?.querySelector('[data-sv-essential-detail-title]');
    const detailText = detail?.querySelector('[data-sv-essential-detail-text]');
    const imageSource = activeHotspot.dataset.svFeatureImage || '';
    const label = activeHotspot.dataset.svFeatureLabel || '';
    const text = activeHotspot.dataset.svFeatureText || '';

    if (detailImage) {
      if (imageSource) {
        const nextImage = getEssentialImage(section, imageSource, detailImage);
        nextImage.alt = label;
        nextImage.hidden = false;
        if (nextImage !== detailImage) detailImage.replaceWith(nextImage);
      } else {
        detailImage.hidden = true;
      }
    }
    if (detailTitle) detailTitle.textContent = label;
    if (detailText) {
      detailText.textContent = text;
      detailText.hidden = !text;
    }
  };

  if (!customElements.get('sv-oddit-header')) {
    customElements.define('sv-oddit-header', class extends HTMLElement {
      connectedCallback() {
        this.megas = [...this.querySelectorAll('[data-sv-mega]')];
        this.megaToggles = [...this.querySelectorAll('[data-sv-mega-toggle]')];
        this.search = this.querySelector('[data-sv-search]');
        this.searchToggle = this.search?.querySelector('summary');
        this.mobileMenu = this.querySelector('[data-sv-mobile-menu]');
        this.mobileToggle = this.querySelector('[data-sv-menu-toggle]');
        this.mobileSearchOpen = this.querySelector('[data-sv-mobile-search-open]');
        this.mobileClose = this.querySelector('[data-sv-menu-close]');
        this.localization = this.querySelector('.sv-oddit-localization');
        this.announcement = this.querySelector('.sv-oddit-announcement');
        this.nav = this.querySelector('.sv-oddit-nav');
        this.updateOverlayOffset = () => {
          const height = this.announcement?.getBoundingClientRect().height || 0;
          const navBottom = this.nav?.getBoundingClientRect().bottom || 0;
          this.style.setProperty('--sv-oddit-overlay-top', `${Math.round(height)}px`);
          this.style.setProperty('--sv-oddit-desktop-panel-top', `${Math.round(navBottom)}px`);
        };
        this.updateOverlayOffset();
        window.addEventListener('resize', this.updateOverlayOffset, { passive: true });

        this.megaHoverMedia = window.matchMedia('(min-width: 901px) and (hover: hover) and (pointer: fine)');
        this.megaToggles.forEach((button) => {
          button.addEventListener('click', (event) => {
            if (this.megaHoverMedia.matches && button.getAttribute('aria-expanded') === 'true') {
              event.preventDefault();
              return;
            }
            this.toggleMega(button);
          });
          button.addEventListener('mouseenter', () => {
            if (!this.megaHoverMedia.matches) return;
            this.clearMegaCloseTimer();
            this.openMega(button);
          });
          button.addEventListener('mouseleave', () => {
            if (this.megaHoverMedia.matches) this.scheduleMegaClose();
          });
          button.addEventListener('focus', () => {
            if (window.matchMedia('(min-width: 901px)').matches) this.openMega(button);
          });
        });
        this.querySelector('[data-sv-search-close]')?.addEventListener('click', () => {
          this.closeSearch();
          this.searchToggle?.focus();
        });
        this.search?.addEventListener('toggle', () => {
          if (!this.search.open) return;
          this.updateOverlayOffset();
          this.closeMega();
          window.setTimeout(() => this.search?.querySelector('input[type="search"]')?.focus(), 0);
        });
        this.prewarmMobileMenu = () => this.loadMobileMenuImages('auto');
        this.mobileToggle?.addEventListener('pointerdown', this.prewarmMobileMenu, { passive: true });
        this.mobileToggle?.addEventListener('focus', this.prewarmMobileMenu);
        this.scheduleMobileMenuImages = () => {
          const warm = () => this.loadMobileMenuImages('low');
          if ('requestIdleCallback' in window) this.mobileImageIdle = window.requestIdleCallback(warm, { timeout: 1000 });
          else this.mobileImageTimer = window.setTimeout(warm, 0);
        };
        if (document.readyState === 'complete') this.scheduleMobileMenuImages();
        else window.addEventListener('load', this.scheduleMobileMenuImages, { once: true });
        this.mobileToggle?.addEventListener('click', () => this.openMobile());
        this.mobileSearchOpen?.addEventListener('click', (event) => {
          event.stopPropagation();
          this.closeMobile();
          this.closeMega();
          if (this.search) this.search.open = true;
          window.setTimeout(() => this.search?.querySelector('input[type="search"]')?.focus(), 0);
        });
        this.mobileClose?.addEventListener('click', () => this.closeMobile());
        const scrollCategoryToTop = (event, link) => {
          if (!link || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
          const destination = new URL(link.href, window.location.href);
          if (destination.origin !== window.location.origin || destination.pathname !== window.location.pathname || destination.search !== window.location.search || destination.hash) return;
          event.preventDefault();
          if (window.location.hash) window.history.replaceState(window.history.state, '', destination.href);
          window.scrollTo({ top: 0, behavior: 'instant' });
        };
        this.mobileMenu?.addEventListener('click', (event) => {
          const link = event.target.closest('a[href]');
          if (link) {
            this.closeMobile();
            scrollCategoryToTop(event, link);
          }
        });
        this.megas.forEach((mega) => {
          mega.addEventListener('click', (event) => {
            if (event.target === mega || event.target.closest('.sv-oddit-mega__intro a[href]')) this.closeMega();
            scrollCategoryToTop(event, event.target.closest('.sv-oddit-mega__intro a[href]'));
          });
          const panel = mega.querySelector('.sv-oddit-mega__panel');
          panel?.addEventListener('mouseenter', () => {
            this.clearMegaCloseTimer();
            this.cancelMegaExit(true);
          });
          panel?.addEventListener('mouseleave', () => {
            if (this.megaHoverMedia.matches) this.scheduleMegaClose();
          });
        });
        this.addEventListener('focusout', () => {
          window.setTimeout(() => {
            const pointerWithinMega = this.megaHoverMedia?.matches && (
              this.megaToggles.some((button) => button.matches(':hover'))
              || this.megas.some((mega) => !mega.hidden && mega.querySelector('.sv-oddit-mega__panel')?.matches(':hover'))
            );
            const searchTransitionActive = performance.now() < (this.ignoreFocusOutUntil || 0);
            if (!this.contains(document.activeElement) && !pointerWithinMega && !searchTransitionActive) this.closeMega();
          }, 0);
        });
        this.addEventListener('keydown', (event) => {
          if (event.key !== 'Escape') return;
          this.closeMega();
          this.closeSearch();
          this.closeMobile();
        });
        document.addEventListener('click', (event) => {
          if (this.localization?.open && !this.localization.contains(event.target)) this.localization.open = false;
          if (this.search?.open && !this.search.contains(event.target)) this.closeSearch();
          if (!event.target.closest('[data-sv-mega], [data-sv-mega-toggle]')) this.closeMega();
        });

        this.cartCount = this.querySelector('#cart-notification-count');
        if (this.cartCount) {
          const updateCartCount = () => {
            const count = Number.parseInt(this.cartCount.textContent, 10) || 0;
            this.cartCount.classList.toggle('is-empty', count === 0);
            const cartLink = this.cartCount.closest('a');
            const drawerCount = document.querySelector('[data-sv-cart-drawer-count]');
            const singular = drawerCount?.dataset.singular || this.dataset.svCartItemSingular || 'item';
            const plural = drawerCount?.dataset.plural || this.dataset.svCartItemPlural || 'items';
            if (cartLink) {
              const labelPrefix = (cartLink.getAttribute('aria-label') || 'Cart').split(',')[0];
              cartLink.setAttribute('aria-label', `${labelPrefix}, ${count} ${count === 1 ? singular : plural}`);
            }
            if (drawerCount) {
              drawerCount.hidden = count === 0;
              drawerCount.textContent = `${count} ${count === 1 ? singular : plural}`;
            }
          };
          updateCartCount();
          new MutationObserver(updateCartCount).observe(this.cartCount, { childList: true, characterData: true, subtree: true });
        }
      }

      toggleMega(button) {
        const group = button.dataset.svMegaTarget;
        const mega = this.megas.find((candidate) => candidate.dataset.svMega === group);
        if (!mega) return;
        const willOpen = mega.hasAttribute('hidden');
        if (willOpen) this.openMega(button);
        else this.closeMega();
      }

      openMega(button) {
        const group = button?.dataset.svMegaTarget;
        const mega = this.megas.find((candidate) => candidate.dataset.svMega === group);
        if (!mega) return;
        if (this.search?.open) this.ignoreFocusOutUntil = performance.now() + 150;
        const alreadyOpen = !mega.hidden;
        const activeMega = this.megas.find((candidate) => !candidate.hidden && candidate !== mega);
        const switching = Boolean(activeMega);
        this.clearMegaCloseTimer();
        this.cancelMegaExit();
        this.closeSearch();
        if (alreadyOpen) {
          this.megaToggles.forEach((candidate) => setExpanded(candidate, candidate === button));
          return;
        }
        if (switching) {
          mega.classList.add('is-switch-target');
          mega.hidden = false;
          this.megas.forEach((candidate) => {
            if (candidate !== mega) candidate.hidden = true;
            candidate.classList.remove('is-closing');
          });
        } else {
          mega.classList.remove('is-switch-target');
          this.megas.forEach((candidate) => {
            candidate.hidden = candidate !== mega;
            candidate.classList.remove('is-closing');
          });
        }
        this.megaToggles.forEach((candidate) => {
          setExpanded(candidate, candidate === button);
        });
      }

      clearMegaCloseTimer() {
        if (!this.megaCloseTimer) return;
        window.clearTimeout(this.megaCloseTimer);
        this.megaCloseTimer = null;
      }

      scheduleMegaClose() {
        this.clearMegaCloseTimer();
        this.megaCloseTimer = window.setTimeout(() => {
          const toggleHovered = this.megaToggles.some((button) => button.matches(':hover'));
          const panelHovered = this.megas.some((mega) => (
            !mega.hidden && mega.querySelector('.sv-oddit-mega__panel')?.matches(':hover')
          ));
          if (toggleHovered || panelHovered) return;
          this.closeMega(true);
        }, 180);
      }

      cancelMegaExit(restoreExpanded = false) {
        if (this.megaExitTimer) {
          window.clearTimeout(this.megaExitTimer);
          this.megaExitTimer = null;
        }
        this.megas.forEach((mega) => mega.classList.remove('is-closing'));
        if (restoreExpanded) {
          this.megaToggles.forEach((button) => {
            const activeMega = this.megas.find((mega) => mega.dataset.svMega === button.dataset.svMegaTarget);
            setExpanded(button, Boolean(activeMega && !activeMega.hidden));
          });
        }
      }

      closeMega(animate = false) {
        this.clearMegaCloseTimer();
        this.cancelMegaExit();
        this.megaToggles.forEach((button) => setExpanded(button, false));
        const openMegas = this.megas.filter((mega) => !mega.hidden);
        const shouldAnimate = animate
          && openMegas.length > 0
          && !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        if (!shouldAnimate) {
          openMegas.forEach((mega) => { mega.hidden = true; });
          return;
        }
        openMegas.forEach((mega) => mega.classList.add('is-closing'));
        this.megaExitTimer = window.setTimeout(() => {
          openMegas.forEach((mega) => {
            mega.hidden = true;
            mega.classList.remove('is-closing');
          });
          this.megaExitTimer = null;
        }, 180);
      }

      closeSearch() {
        if (this.search) this.search.open = false;
      }

      loadMobileMenuImages(priority) {
        if (!this.isConnected || !window.matchMedia('(max-width: 1100px)').matches) return;
        // Warm the existing responsive sources, including collapsed categories, after page load.
        this.mobileMenu?.querySelectorAll('.sv-oddit-mobile-category-group img').forEach((image) => {
          image.fetchPriority = priority === 'auto' || image.fetchPriority === 'auto' && image.loading === 'eager' ? 'auto' : 'low';
          if (image.loading === 'eager') return;
          image.loading = 'eager';
          image.decode?.().catch(() => {});
        });
      }

      openMobile() {
        if (!this.mobileMenu) return;
        this.loadMobileMenuImages('auto');
        this.updateOverlayOffset?.();
        this.mobileMenu.hidden = false;
        document.body.style.overflow = 'hidden';
        setExpanded(this.mobileToggle, true);
        this.mobileClose?.focus();
      }

      disconnectedCallback() {
        this.clearMegaCloseTimer();
        this.cancelMegaExit();
        window.removeEventListener('resize', this.updateOverlayOffset);
        window.removeEventListener('load', this.scheduleMobileMenuImages);
        window.cancelIdleCallback?.(this.mobileImageIdle);
        window.clearTimeout(this.mobileImageTimer);
        this.mobileToggle?.removeEventListener('pointerdown', this.prewarmMobileMenu);
        this.mobileToggle?.removeEventListener('focus', this.prewarmMobileMenu);
      }

      closeMobile() {
        if (!this.mobileMenu || this.mobileMenu.hasAttribute('hidden')) return;
        this.mobileMenu.hidden = true;
        document.body.style.overflow = '';
        setExpanded(this.mobileToggle, false);
        this.mobileToggle?.focus();
      }
    });
  }

  if (!customElements.get('sv-oddit-faq')) {
    customElements.define('sv-oddit-faq', class extends HTMLElement {
      connectedCallback() {
        if (this.dataset.svOdditReady === 'true') return;
        this.dataset.svOdditReady = 'true';
        this.animations = new WeakMap();
        this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
        this.items = [...this.querySelectorAll('details.sv-oddit-accordion')];
        this.items.forEach((item) => {
          const summary = item.querySelector(':scope > summary');
          const content = item.querySelector(':scope > .sv-oddit-accordion__content');
          if (!summary || !content) return;

          item.classList.toggle('is-expanded', item.open);
          summary.setAttribute('aria-expanded', item.open ? 'true' : 'false');
          summary.addEventListener('click', (event) => {
            event.preventDefault();
            this.toggleItem(item);
          });
        });
      }

      toggleItem(item) {
        const expand = !item.classList.contains('is-expanded');

        if (expand) {
          this.items.forEach((otherItem) => {
            if (otherItem !== item && otherItem.classList.contains('is-expanded')) {
              this.animateItem(otherItem, false);
            }
          });
        }

        this.animateItem(item, expand);
      }

      animateItem(item, expand) {
        const summary = item.querySelector(':scope > summary');
        const content = item.querySelector(':scope > .sv-oddit-accordion__content');
        if (!summary || !content) return;

        const runningAnimation = this.animations.get(item);
        const wasOpen = item.open;
        const currentHeight = wasOpen ? content.getBoundingClientRect().height : 0;
        const currentStyles = window.getComputedStyle(content);
        const parsedCurrentOpacity = Number.parseFloat(currentStyles.opacity);
        const currentOpacity = wasOpen && Number.isFinite(parsedCurrentOpacity) ? parsedCurrentOpacity : 0;
        const currentTransform = wasOpen && currentStyles.transform !== 'none'
          ? currentStyles.transform
          : expand ? 'translateY(-4px)' : 'translateY(0)';
        const currentPaddingTop = wasOpen ? Number.parseFloat(currentStyles.paddingTop) || 0 : 0;
        const currentPaddingBottom = wasOpen ? Number.parseFloat(currentStyles.paddingBottom) || 0 : 0;
        runningAnimation?.cancel();
        content.style.removeProperty('height');
        content.style.removeProperty('opacity');
        content.style.removeProperty('transform');
        content.style.removeProperty('padding-top');
        content.style.removeProperty('padding-bottom');

        const naturalStyles = window.getComputedStyle(content);
        const parsedNaturalOpacity = Number.parseFloat(naturalStyles.opacity);
        const naturalOpacity = Number.isFinite(parsedNaturalOpacity) ? parsedNaturalOpacity : 1;
        const naturalPaddingTop = Number.parseFloat(naturalStyles.paddingTop) || 0;
        const naturalPaddingBottom = Number.parseFloat(naturalStyles.paddingBottom) || 0;

        item.classList.toggle('is-expanded', expand);
        item.classList.toggle('is-closing', !expand);
        summary.setAttribute('aria-expanded', expand ? 'true' : 'false');

        if (this.reducedMotion.matches || typeof content.animate !== 'function') {
          item.open = expand;
          item.classList.remove('is-animating', 'is-closing');
          content.style.removeProperty('height');
          content.style.removeProperty('opacity');
          content.style.removeProperty('transform');
          content.style.removeProperty('padding-top');
          content.style.removeProperty('padding-bottom');
          return;
        }

        if (expand) {
          if (!item.open) item.open = true;
          content.style.paddingTop = `${naturalPaddingTop}px`;
          content.style.paddingBottom = `${naturalPaddingBottom}px`;
          content.style.height = 'auto';
        }

        const targetHeight = expand ? content.getBoundingClientRect().height : 0;
        const targetOpacity = expand ? naturalOpacity : 0;
        const targetPaddingTop = expand ? naturalPaddingTop : 0;
        const targetPaddingBottom = expand ? naturalPaddingBottom : 0;
        content.style.height = `${currentHeight}px`;
        content.style.opacity = `${currentOpacity}`;
        content.style.transform = currentTransform;
        content.style.paddingTop = `${currentPaddingTop}px`;
        content.style.paddingBottom = `${currentPaddingBottom}px`;
        item.classList.add('is-animating');

        const animation = content.animate([
          {
            height: `${currentHeight}px`,
            opacity: currentOpacity,
            transform: currentTransform,
            paddingTop: `${currentPaddingTop}px`,
            paddingBottom: `${currentPaddingBottom}px`
          },
          {
            height: `${targetHeight}px`,
            opacity: targetOpacity,
            transform: expand ? 'translateY(0)' : 'translateY(-4px)',
            paddingTop: `${targetPaddingTop}px`,
            paddingBottom: `${targetPaddingBottom}px`
          }
        ], {
          duration: 300,
          easing: 'cubic-bezier(.22, 1, .36, 1)',
          fill: 'forwards'
        });

        this.animations.set(item, animation);
        animation.finished.then(() => {
          if (this.animations.get(item) !== animation) return;
          content.style.height = `${targetHeight}px`;
          content.style.opacity = `${targetOpacity}`;
          content.style.transform = expand ? 'translateY(0)' : 'translateY(-4px)';
          content.style.paddingTop = `${targetPaddingTop}px`;
          content.style.paddingBottom = `${targetPaddingBottom}px`;
          animation.cancel();
          if (!expand) item.open = false;
          content.style.removeProperty('height');
          content.style.removeProperty('opacity');
          content.style.removeProperty('transform');
          content.style.removeProperty('padding-top');
          content.style.removeProperty('padding-bottom');
          item.classList.remove('is-animating', 'is-closing');
          this.animations.delete(item);
        }).catch(() => {});
      }
    });
  }

  if (!customElements.get('sv-oddit-slider')) {
    customElements.define('sv-oddit-slider', class extends HTMLElement {
      connectedCallback() {
        this.track = this.querySelector('[data-sv-slider-track]');
        this.centered = Boolean(this.track?.hasAttribute('data-sv-slider-center'));
        this.startLocked = Boolean(this.track?.hasAttribute('data-sv-slider-start-lock'));
        this.softCenter = Boolean(this.track?.hasAttribute('data-sv-slider-soft-center'));
        this.desktopSnap = this.track?.dataset.svSliderDesktopSnap || 'center';
        this.querySelector('[data-sv-slider-prev]')?.addEventListener('click', () => this.move(-1));
        this.querySelector('[data-sv-slider-next]')?.addEventListener('click', () => this.move(1));
        this.progress = this.querySelector('[data-sv-slider-progress]');
        this.progressRefresh = () => this.updateProgress();
        this.track?.addEventListener('scroll', () => {
          this.updateProgress();
          if (this.startLocked && !this.usesNativeScroll() && !('onscrollend' in this.track) && !this.scrollAnimation && !this.startLockRunning) {
            this.scheduleStartLock();
          }
          if (this.softCenter && !this.usesNativeScroll() && !('onscrollend' in this.track) && !this.scrollAnimation && !this.track.classList.contains('is-dragging')) {
            window.clearTimeout(this.softCenterTimer);
            this.softCenterTimer = window.setTimeout(() => this.settleNearestCard(this.track.scrollLeft), 130);
          }
        }, { passive: true });
        if (this.softCenter && this.track && 'onscrollend' in this.track) {
          this.handleSoftCenterEnd = () => {
            if (this.usesNativeScroll() || this.scrollAnimation || this.track.classList.contains('is-dragging')) return;
            this.settleNearestCard(this.track.scrollLeft);
          };
          this.track.addEventListener('scrollend', this.handleSoftCenterEnd);
        }
        if (this.startLocked && 'onscrollend' in this.track) {
          this.track.addEventListener('scrollend', () => {
            if (!this.usesNativeScroll() && !this.scrollAnimation && !this.startLockRunning) this.lockStartBoundary();
          });
        }
        this.enableDrag();
        if (this.track?.hasAttribute('data-sv-slider-peek') && window.matchMedia('(min-width: 901px)').matches) {
          requestAnimationFrame(() => { this.track.scrollLeft = (this.track.firstElementChild?.getBoundingClientRect().width || 0) * .22; });
        }
        requestAnimationFrame(() => {
          if (this.centered && window.matchMedia('(min-width: 901px)').matches) this.centerNearestCard('auto');
          this.updateProgress();
        });
        /* Product media and Shopify-hosted GIF/video posters can finish sizing
           after the custom element connects. Recalculate the indicator when
           that layout settles so its initial state never renders as 100%. */
        if (this.track && 'ResizeObserver' in window) {
          this.progressResizeObserver = new ResizeObserver(this.progressRefresh);
          this.progressResizeObserver.observe(this.track);
          [...this.track.children].forEach((card) => this.progressResizeObserver.observe(card));
        }
        window.addEventListener('load', this.progressRefresh, { once: true });
        this.progressRefreshTimer = window.setTimeout(this.progressRefresh, 300);
      }

      disconnectedCallback() {
        window.clearTimeout(this.progressRefreshTimer);
        window.clearTimeout(this.softCenterTimer);
        window.clearTimeout(this.startLockTimer);
        this.cancelScrollAnimation();
        window.removeEventListener('load', this.progressRefresh);
        if (this.dragStopHandler) {
          window.removeEventListener('pointerup', this.dragStopHandler);
          window.removeEventListener('pointercancel', this.dragStopHandler);
        }
        if (this.handleSoftCenterEnd) this.track?.removeEventListener('scrollend', this.handleSoftCenterEnd);
        this.progressResizeObserver?.disconnect();
      }

      usesNativeScroll() {
        return this.touchScrolling || window.matchMedia('(max-width: 900px), (pointer: coarse)').matches;
      }

      enableDrag() {
        if (!this.track) return;
        let active = false;
        let startX = 0;
        let startScroll = 0;
        let moved = false;
        let lastX = 0;
        let lastTime = 0;
        let velocity = 0;
        this.track.querySelectorAll('img, a, video').forEach((element) => element.setAttribute('draggable', 'false'));
        this.track.addEventListener('pointerdown', (event) => {
          this.touchScrolling = event.pointerType === 'touch';
          if (this.touchScrolling) {
            window.clearTimeout(this.startLockTimer);
            window.clearTimeout(this.softCenterTimer);
            this.cancelScrollAnimation();
            this.startLockRunning = false;
            moved = false;
            return;
          }
          if (event.target.closest('button, a, input, select, textarea')) return;
          const targetVideo = event.target.closest('video');
          if (targetVideo && (targetVideo.controls || targetVideo.closest('[data-sv-video-card]')?.classList.contains('is-playing'))) return;
          active = true;
          startX = event.clientX;
          startScroll = this.track.scrollLeft;
          lastX = event.clientX;
          lastTime = performance.now();
          velocity = 0;
          moved = false;
          window.clearTimeout(this.startLockTimer);
          this.cancelScrollAnimation();
          this.startLockRunning = false;
          this.track.classList.add('is-dragging');
          this.track.setPointerCapture?.(event.pointerId);
        });
        this.track.addEventListener('pointermove', (event) => {
          if (!active) return;
          if (Math.abs(event.clientX - startX) > 3) moved = true;
          const now = performance.now();
          const elapsed = Math.max(1, now - lastTime);
          velocity = velocity * .72 + ((event.clientX - lastX) / elapsed) * .28;
          lastX = event.clientX;
          lastTime = now;
          this.track.scrollLeft = startScroll - (event.clientX - startX);
          event.preventDefault();
        });
        const stop = (event) => {
          if (!active) return;
          active = false;
          this.track.classList.remove('is-dragging');
          if (event?.pointerId != null && this.track.hasPointerCapture?.(event.pointerId)) this.track.releasePointerCapture(event.pointerId);
          if (moved && window.matchMedia('(min-width: 901px)').matches) {
            const projectedScroll = this.softCenter
              ? this.track.scrollLeft
              : this.track.scrollLeft - velocity * 170;
            this.settleNearestCard(projectedScroll);
          }
        };
        this.track.addEventListener('pointerup', stop);
        this.track.addEventListener('pointercancel', stop);
        this.track.addEventListener('lostpointercapture', stop);
        this.dragStopHandler = stop;
        window.addEventListener('pointerup', stop);
        window.addEventListener('pointercancel', stop);
        this.track.addEventListener('click', (event) => {
          if (!moved) return;
          event.preventDefault();
          event.stopPropagation();
          moved = false;
        }, true);
      }

      move(direction) {
        if (!this.track) return;
        const cards = [...this.track.children];
        if (!cards.length) return;
        const viewportCenter = this.track.scrollLeft + this.track.clientWidth / 2;
        const closestIndex = cards.reduce((closest, card, index) => {
          const distance = Math.abs(this.cardTrackOffset(card) + card.offsetWidth / 2 - viewportCenter);
          return distance < closest.distance ? { index, distance } : closest;
        }, { index: 0, distance: Infinity }).index;
        const target = cards[Math.max(0, Math.min(cards.length - 1, closestIndex + direction))];
        this.scrollCardToCenter(target, window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth');
      }

      scheduleStartLock() {
        if (this.usesNativeScroll() || this.scrollAnimation || this.startLockRunning) return;
        window.clearTimeout(this.startLockTimer);
        this.startLockTimer = window.setTimeout(() => this.lockStartBoundary(), 160);
      }

      lockStartBoundary() {
        if (!this.track || this.usesNativeScroll() || this.track.classList.contains('is-dragging') || this.scrollAnimation || this.startLockRunning) return;
        const firstCard = this.track.firstElementChild;
        if (!firstCard) return;
        const threshold = Math.min(180, firstCard.offsetWidth * .45);
        if (this.track.scrollLeft > 0 && this.track.scrollLeft <= 2) {
          this.cancelScrollAnimation();
          this.track.scrollLeft = 0;
          return;
        }
        if (this.track.scrollLeft > .5 && this.track.scrollLeft < threshold) {
          this.startLockRunning = true;
          this.animateScrollTo(0, () => {
            this.track.scrollLeft = 0;
            this.startLockRunning = false;
          });
        }
      }

      settleNearestCard(projectedScroll = this.track?.scrollLeft || 0) {
        if (!this.track || this.usesNativeScroll()) return;
        const maximum = Math.max(0, this.track.scrollWidth - this.track.clientWidth);
        const cards = [...this.track.children];
        if (!cards.length) return;
        const clampedScroll = Math.max(0, Math.min(maximum, projectedScroll));
        if (this.softCenter && this.desktopSnap === 'start' && window.matchMedia('(min-width: 901px)').matches) {
          const targetPosition = cards.reduce((closest, card) => {
            const position = Math.max(0, Math.min(maximum, this.cardTrackOffset(card)));
            const distance = Math.abs(position - clampedScroll);
            return !closest || distance < closest.distance ? { position, distance } : closest;
          }, null)?.position;
          this.scrollCardToPosition(targetPosition ?? clampedScroll, 'smooth');
          return;
        }
        if (this.softCenter && maximum > 0) {
          const edgeThreshold = Math.min(96, (cards[0]?.offsetWidth || 0) * .32);
          if (clampedScroll <= edgeThreshold) {
            this.scrollCardToPosition(0, 'smooth');
            return;
          }
          if (clampedScroll >= maximum - edgeThreshold) {
            this.scrollCardToPosition(maximum, 'smooth');
            return;
          }
        }
        const projectedCenter = clampedScroll + this.track.clientWidth / 2;
        const target = cards.reduce((closest, card) => {
          const distance = Math.abs(this.cardTrackOffset(card) + card.offsetWidth / 2 - projectedCenter);
          return !closest || distance < closest.distance ? { card, distance } : closest;
        }, null)?.card;
        this.scrollCardToCenter(target, 'smooth');
      }

      centerNearestCard(behavior = 'smooth') {
        if (!this.track) return;
        const cards = [...this.track.children];
        if (!cards.length) return;
        const viewportCenter = this.track.scrollLeft + this.track.clientWidth / 2;
        const target = cards.reduce((closest, card) => {
          const distance = Math.abs(this.cardTrackOffset(card) + card.offsetWidth / 2 - viewportCenter);
          return !closest || distance < closest.distance ? { card, distance } : closest;
        }, null)?.card;
        this.scrollCardToCenter(target, behavior);
      }

      scrollCardToCenter(card, behavior = 'smooth') {
        if (!this.track || !card) return;
        const desired = this.cardTrackOffset(card) - (this.track.clientWidth - card.offsetWidth) / 2;
        const maximum = Math.max(0, this.track.scrollWidth - this.track.clientWidth);
        const destination = Math.max(0, Math.min(maximum, desired));
        this.scrollCardToPosition(destination, behavior);
      }

      cardTrackOffset(card) {
        if (!this.track || !card) return 0;
        const trackBox = this.track.getBoundingClientRect();
        const cardBox = card.getBoundingClientRect();
        return cardBox.left - trackBox.left + this.track.scrollLeft;
      }

      scrollCardToPosition(destination, behavior = 'smooth') {
        if (!this.track) return;
        if (this.usesNativeScroll()) {
          this.cancelScrollAnimation();
          this.track.scrollTo({ left: destination, behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : behavior });
          return;
        }
        if (behavior === 'auto' || window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
          this.cancelScrollAnimation();
          this.track.scrollLeft = destination;
          return;
        }
        this.animateScrollTo(destination);
      }

      animateScrollTo(destination, onComplete) {
        if (!this.track) return;
        if (!onComplete) this.startLockRunning = false;
        this.cancelScrollAnimation();
        this.track.classList.add('is-programmatic-scrolling');
        const start = this.track.scrollLeft;
        const distance = destination - start;
        if (Math.abs(distance) < 1) {
          this.track.scrollLeft = destination;
          this.track.classList.remove('is-programmatic-scrolling');
          onComplete?.();
          return;
        }
        const startedAt = performance.now();
        const isUgcCarousel = Boolean(this.closest('[id$="__sv_oddit_ugc"]'));
        const duration = isUgcCarousel
          ? Math.min(620, Math.max(400, Math.abs(distance) * .56))
          : Math.min(520, Math.max(300, Math.abs(distance) * .42));
        const tick = (now) => {
          const progress = Math.min(1, (now - startedAt) / duration);
          const eased = isUgcCarousel
            ? (progress < .5
              ? 4 * progress * progress * progress
              : 1 - Math.pow(-2 * progress + 2, 3) / 2)
            : 1 - Math.pow(1 - progress, 3);
          this.track.scrollLeft = start + distance * eased;
          if (progress < 1) {
            this.scrollAnimation = requestAnimationFrame(tick);
          } else {
            this.track.scrollLeft = destination;
            this.scrollAnimation = null;
            this.track.classList.remove('is-programmatic-scrolling');
            onComplete?.();
          }
        };
        this.scrollAnimation = requestAnimationFrame(tick);
      }

      cancelScrollAnimation() {
        if (this.scrollAnimation) cancelAnimationFrame(this.scrollAnimation);
        this.scrollAnimation = null;
        this.track?.classList.remove('is-programmatic-scrolling');
      }

      updateProgress() {
        if (!this.track) return;
        const scrollWidth = this.track.scrollWidth;
        const clientWidth = this.track.clientWidth;
        const max = Math.max(1, scrollWidth - clientWidth);
        const ratio = Math.min(1, Math.max(0, this.track.scrollLeft / max));
        const edgeFade = this.track.closest('[data-sv-slider-edge-fade]');
        edgeFade?.classList.toggle('is-at-end', this.track.scrollLeft >= max - 2);
        if (!this.progress) return;
        const cardCount = this.track.children.length;
        const hasMeasuredOverflow = scrollWidth > clientWidth + 1 && clientWidth > 0;
        const progressByItem = this.track.hasAttribute('data-sv-slider-progress-by-item');
        const width = progressByItem && cardCount > 1
          ? 100 / cardCount
          : hasMeasuredOverflow
            ? Math.min(100, clientWidth / scrollWidth * 100)
            : (cardCount > 1 ? 100 / cardCount : 100);
        this.progress.style.width = `${width}%`;
        this.progress.style.transform = `translateX(${ratio * (100 / Math.max(width, 1) - 1) * 100}%)`;
      }
    });
  }

  if (!customElements.get('sv-oddit-ugc-mirror')) {
    customElements.define('sv-oddit-ugc-mirror', class extends HTMLElement {
      connectedCallback() {
        this.track = this.querySelector('[data-sv-ugc-mirror-track]');
        this.fallback = this.querySelector('[data-sv-ugc-mirror-fallback]');
        this.handleSectionLoad = () => this.renderMirror();
        document.addEventListener('shopify:section:load', this.handleSectionLoad);
        this.renderMirror();
      }

      disconnectedCallback() {
        document.removeEventListener('shopify:section:load', this.handleSectionLoad);
      }

      renderMirror() {
        if (!this.track) return;
        const sourceTrack = document.querySelector('[data-sv-product-proof-source] [data-sv-slider-track]')
          || document.querySelector('[id$="__sv_oddit_ugc"] [data-sv-slider-track]');
        const sourceCards = sourceTrack ? [...sourceTrack.children] : [];
        const fragment = document.createDocumentFragment();

        if (sourceCards.length) {
          ['landscape', 'portrait', 'square'].forEach((ratio) => {
            this.track.classList.toggle(`sv-oddit-card-grid--${ratio}`, sourceTrack.classList.contains(`sv-oddit-card-grid--${ratio}`));
          });
          this.track.style.setProperty('--sv-ugc-mirror-gap', getComputedStyle(sourceTrack).columnGap || '12px');
          this.style.setProperty('--sv-card-radius', getComputedStyle(sourceCards[0]).borderRadius || '8px');
          sourceCards.forEach((sourceCard) => fragment.appendChild(this.cloneCard(sourceCard)));
        } else if (this.fallback?.content) {
          fragment.appendChild(this.fallback.content.cloneNode(true));
        }

        this.track.replaceChildren(fragment);
        this.track.setAttribute('aria-busy', 'false');
        this.classList.add('is-ready');

        const slider = this.querySelector('sv-oddit-slider');
        requestAnimationFrame(() => slider?.updateProgress?.());
      }

      cloneCard(sourceCard) {
        const clone = sourceCard.cloneNode(true);
        clone.removeAttribute('id');
        clone.removeAttribute('data-shopify-editor-block');
        clone.querySelectorAll('[id]').forEach((element) => element.removeAttribute('id'));
        clone.querySelectorAll('[data-shopify-editor-block]').forEach((element) => element.removeAttribute('data-shopify-editor-block'));
        clone.querySelectorAll('video').forEach((video) => {
          video.preload = 'none';
          video.controls = false;
          video.pause();
        });
        return clone;
      }
    });
  }

  if (!customElements.get('sv-oddit-newsletter')) {
    customElements.define('sv-oddit-newsletter', class extends HTMLElement {
      connectedCallback() {
        this.modal = this.closest('.sv-oddit-footer')?.querySelector('[data-sv-newsletter-modal]');
        this.modal?.querySelector('[data-sv-newsletter-close]')?.addEventListener('click', () => this.modal.close());
        this.modal?.addEventListener('click', (event) => { if (event.target === this.modal) this.modal.close(); });
        const currentUrl = new URL(window.location.href);
        if (currentUrl.hash.startsWith('#SvOdditSignup-')) return;
        const postedByShopify = currentUrl.searchParams.get('customer_posted') === 'true';
        const status = this.querySelector('[data-sv-newsletter-status]');
        const statusText = status?.textContent?.trim() || '';
        const alreadySubscribed = /already|has been taken|already subscribed|reeds|\bal\b|déjà|bereits/i.test(statusText);
        if (this.querySelector('[data-sv-newsletter-posted]') || postedByShopify || alreadySubscribed) {
          this.querySelector('form')?.reset();
          if (status) status.hidden = true;
          this.openDialog(this.modal);
          if (postedByShopify || currentUrl.hash === '#contact_form') {
            currentUrl.searchParams.delete('customer_posted');
            if (currentUrl.hash === '#contact_form') currentUrl.hash = '';
            window.history.replaceState({}, '', `${currentUrl.pathname}${currentUrl.search}${currentUrl.hash}`);
          }
        } else if (statusText && status) {
          status.hidden = false;
          status.setAttribute('tabindex', '-1');
          status.focus({ preventScroll: false });
        }
      }

      openDialog(dialog) {
        if (!dialog || dialog.open) return;
        if (typeof dialog.showModal === 'function') dialog.showModal();
        else dialog.setAttribute('open', '');
      }
    });
  }

  if (!window.svOdditCartQuantityBound) {
    window.svOdditCartQuantityBound = true;
    document.addEventListener('click', (event) => {
      const quantityButton = event.target.closest('[data-sv-cart-quantity]');
      if (!quantityButton) return;
      const quantityWrapper = quantityButton.closest('.sv-oddit-cart__qty');
      const input = quantityWrapper?.querySelector('input[name="updates[]"]');
      if (!input) return;
      const direction = Number(quantityButton.dataset.svCartQuantity);
      const minimum = Number(input.min || 0);
      input.value = Math.max(minimum, Number(input.value) + direction);
      input.dispatchEvent(new Event('change', { bubbles: true }));
    });
  }

  if (!window.svOdditMediaControlsBound) {
    window.svOdditMediaControlsBound = true;
    document.addEventListener('click', (event) => {
      const playButton = event.target.closest('[data-sv-video-play]');
      if (playButton) {
        const card = playButton.closest('[data-sv-video-card]');
        const video = card?.querySelector('video');
        if (!video) return;
        const sources = video.querySelector('template[data-sv-home-card-sources]');
        if (sources && !video.dataset.svHomeCardLoaded) {
          sources.content.querySelectorAll('source').forEach((source) => video.append(source.cloneNode(true)));
          const poster = card.querySelector('.sv-oddit-home-card-poster');
          if (poster?.currentSrc) video.poster = poster.currentSrc;
          video.dataset.svHomeCardLoaded = 'true';
          video.load();
        }
        video.controls = true;
        video.defaultMuted = false;
        video.muted = false;
        video.volume = 1;
        video.removeAttribute('muted');
        const playPromise = video.play();
        if (playPromise?.catch) playPromise.catch(() => {});
        card.classList.add('is-playing');
        return;
      }

      const hotspot = event.target.closest('[data-sv-feature-index]');
      if (hotspot) {
        const section = hotspot.closest('.sv-oddit-essentials');
        selectEssential(section, Number(hotspot.dataset.svFeatureIndex || 0));
        return;
      }

      const essentialArrow = event.target.closest('[data-sv-essential-prev], [data-sv-essential-next]');
      if (!essentialArrow) return;
      const section = essentialArrow.closest('.sv-oddit-essentials');
      const current = Number(section?.dataset.svEssentialIndex || 0);
      selectEssential(section, current + (essentialArrow.hasAttribute('data-sv-essential-next') ? 1 : -1));
    });

    /* Browsers block autoplay with sound. Keep autoplay media policy-safe, but
       make every visitor-initiated video interaction start at normal volume. */
    document.addEventListener('pointerdown', (event) => {
      const video = event.target.closest?.('video');
      if (!video) return;
      video.defaultMuted = false;
      video.muted = false;
      video.volume = 1;
      video.removeAttribute('muted');
    }, true);
  }

  // Load and decode feature images near the section, without competing with the page hero.
  const essentialImageObserver = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (!entry.isIntersecting) return;
      warmEssentialImages(entry.target);
      essentialImageObserver.unobserve(entry.target);
    });
  }, { rootMargin: '600px 0px' });
  document.querySelectorAll('.sv-oddit-essentials').forEach((section) => {
    selectEssential(section, Number(section.dataset.svEssentialDefaultIndex || 0));
    if (section.querySelector('[data-sv-essential-detail-image]')) essentialImageObserver.observe(section);
  });
  document.querySelectorAll('.sv-oddit-essentials').forEach((section) => {
    section.querySelectorAll('.sv-oddit-essentials__hero').forEach((swipeSurface) => {
      let swipeStartX = 0;
      let swipePointer = null;
      swipeSurface.addEventListener('pointerdown', (event) => {
        if (event.target.closest('button, a')) return;
        swipePointer = event.pointerId;
        swipeStartX = event.clientX;
        swipeSurface.setPointerCapture?.(event.pointerId);
      });
      swipeSurface.addEventListener('pointerup', (event) => {
        if (swipePointer !== event.pointerId) return;
        const distance = event.clientX - swipeStartX;
        swipePointer = null;
        if (Math.abs(distance) < 40) return;
        const current = Number(section.dataset.svEssentialIndex || 0);
        selectEssential(section, current + (distance < 0 ? 1 : -1));
      });
      swipeSurface.addEventListener('pointercancel', () => { swipePointer = null; });
    });
  });
})();
